import jsPDF from 'jspdf'
import * as XLSX from 'xlsx'

export interface CookieCutter {
  id: string
  name: string
  category?: string | null
  description?: string | null
  photo_url?: string | null
  created_at: string
}

export interface StorageBin {
  id: string
  name: string
  description?: string | null
  created_at: string
  cookie_cutters?: CookieCutter[]
}

export const exportBinToPDF = (bin: StorageBin) => {
  try {
    const pdf = new jsPDF()
    const margin = 20
    let yPosition = margin
    const pageHeight = pdf.internal.pageSize.getHeight()
    
    // Title
    pdf.setFontSize(20)
    pdf.text(`Storage Bin: ${bin.name}`, margin, yPosition)
    yPosition += 15
    
    // Date
    pdf.setFontSize(10)
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition)
    yPosition += 15
    
    // Bin Details
    pdf.setFontSize(14)
    pdf.text('Bin Information:', margin, yPosition)
    yPosition += 10
    
    pdf.setFontSize(10)
    pdf.text(`Name: ${bin.name}`, margin, yPosition)
    yPosition += 8
    
    if (bin.description) {
      pdf.text(`Description: ${bin.description}`, margin, yPosition)
      yPosition += 8
    }
    
    pdf.text(`Created: ${new Date(bin.created_at).toLocaleDateString()}`, margin, yPosition)
    yPosition += 8
    
    pdf.text(`Total Cookie Cutters: ${bin.cookie_cutters?.length || 0}`, margin, yPosition)
    yPosition += 20
    
    // Cookie Cutters
    if (bin.cookie_cutters && bin.cookie_cutters.length > 0) {
      pdf.setFontSize(14)
      pdf.text('Cookie Cutters:', margin, yPosition)
      yPosition += 15
      
      bin.cookie_cutters.forEach((cutter, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 50) {
          pdf.addPage()
          yPosition = margin
        }
        
        pdf.setFontSize(12)
        pdf.text(`${index + 1}. ${cutter.name}`, margin, yPosition)
        yPosition += 10
        
        pdf.setFontSize(10)
        
        if (cutter.category) {
          pdf.text(`   Category: ${cutter.category}`, margin, yPosition)
          yPosition += 8
        }
        
        if (cutter.description) {
          pdf.text(`   Description: ${cutter.description}`, margin, yPosition)
          yPosition += 8
        }
        
        pdf.text(`   Added: ${new Date(cutter.created_at).toLocaleDateString()}`, margin, yPosition)
        yPosition += 15
      })
    } else {
      pdf.setFontSize(12)
      pdf.text('No cookie cutters in this bin.', margin, yPosition)
    }
    
    // Download
    const fileName = `${bin.name.replace(/[^a-zA-Z0-9]/g, '-')}-inventory-${new Date().toISOString().split('T')[0]}.pdf`
    pdf.save(fileName)
  } catch (error) {
    console.error('Error generating PDF:', error)
    throw error
  }
}

export const exportBinToExcel = (bin: StorageBin) => {
  try {
    const workbook = XLSX.utils.book_new()
    
    // Bin Info Sheet
    const binInfo = [
      ['Storage Bin Report'],
      [''],
      ['Bin Name', bin.name],
      ['Description', bin.description || 'N/A'],
      ['Created Date', new Date(bin.created_at).toLocaleDateString()],
      ['Total Cookie Cutters', bin.cookie_cutters?.length || 0],
      ['Report Generated', new Date().toLocaleString()],
    ]
    
    const binInfoSheet = XLSX.utils.aoa_to_sheet(binInfo)
    
    // Set column widths
    binInfoSheet['!cols'] = [
      { wch: 20 },
      { wch: 40 }
    ]
    
    XLSX.utils.book_append_sheet(workbook, binInfoSheet, 'Bin Info')
    
    // Cookie Cutters Sheet
    if (bin.cookie_cutters && bin.cookie_cutters.length > 0) {
      const cutterData = bin.cookie_cutters.map((cutter, index) => ({
        'No.': index + 1,
        'Name': cutter.name,
        'Category': cutter.category || 'Uncategorized',
        'Description': cutter.description || '',
        'Added Date': new Date(cutter.created_at).toLocaleDateString(),
        'Has Photo': cutter.photo_url ? 'Yes' : 'No'
      }))
      
      const cutterSheet = XLSX.utils.json_to_sheet(cutterData)
      
      // Set column widths
      cutterSheet['!cols'] = [
        { wch: 5 },   // No.
        { wch: 25 },  // Name
        { wch: 15 },  // Category
        { wch: 40 },  // Description
        { wch: 12 },  // Added Date
        { wch: 10 }   // Has Photo
      ]
      
      XLSX.utils.book_append_sheet(workbook, cutterSheet, 'Cookie Cutters')
    }
    
    // Download
    const fileName = `${bin.name.replace(/[^a-zA-Z0-9]/g, '-')}-inventory-${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
  } catch (error) {
    console.error('Error generating Excel:', error)
    throw error
  }
}

export const exportAllBinsToPDF = (bins: StorageBin[]) => {
  try {
    const pdf = new jsPDF()
    const margin = 20
    let yPosition = margin
    const pageHeight = pdf.internal.pageSize.getHeight()
    
    // Title
    pdf.setFontSize(20)
    pdf.text('Complete Storage Bins Inventory', margin, yPosition)
    yPosition += 15
    
    // Date and summary
    pdf.setFontSize(10)
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition)
    yPosition += 8
    
    const totalCutters = bins.reduce((sum, bin) => sum + (bin.cookie_cutters?.length || 0), 0)
    pdf.text(`Total Bins: ${bins.length}`, margin, yPosition)
    yPosition += 8
    pdf.text(`Total Cookie Cutters: ${totalCutters}`, margin, yPosition)
    yPosition += 20
    
    // Detailed contents for each bin
    bins.forEach((bin, binIndex) => {
      // Check if we need a new page for bin header
      if (yPosition > pageHeight - 60) {
        pdf.addPage()
        yPosition = margin
      }
      
      // Bin header
      pdf.setFontSize(16)
      pdf.text(`Storage Bin ${binIndex + 1}: ${bin.name}`, margin, yPosition)
      yPosition += 12
      
      pdf.setFontSize(10)
      if (bin.description) {
        pdf.text(`Description: ${bin.description}`, margin, yPosition)
        yPosition += 8
      }
      
      pdf.text(`Created: ${new Date(bin.created_at).toLocaleDateString()}`, margin, yPosition)
      yPosition += 8
      pdf.text(`Cookie Cutters: ${bin.cookie_cutters?.length || 0}`, margin, yPosition)
      yPosition += 12
      
      // List all cookie cutters in this bin
      if (bin.cookie_cutters && bin.cookie_cutters.length > 0) {
        pdf.setFontSize(12)
        pdf.text('Contents:', margin, yPosition)
        yPosition += 10
        
        bin.cookie_cutters.forEach((cutter, cutterIndex) => {
          // Check if we need a new page for cutter details
          if (yPosition > pageHeight - 30) {
            pdf.addPage()
            yPosition = margin
          }
          
          pdf.setFontSize(11)
          pdf.text(`${cutterIndex + 1}. ${cutter.name}`, margin + 10, yPosition)
          yPosition += 8
          
          pdf.setFontSize(9)
          
          if (cutter.category) {
            pdf.text(`Category: ${cutter.category}`, margin + 20, yPosition)
            yPosition += 6
          }
          
          if (cutter.description) {
            // Handle long descriptions by wrapping text
            const maxWidth = pdf.internal.pageSize.getWidth() - margin * 2 - 20
            const wrappedText = pdf.splitTextToSize(cutter.description, maxWidth)
            pdf.text(`Description: ${wrappedText[0]}`, margin + 20, yPosition)
            yPosition += 6
            
            // Add additional lines if description wraps
            for (let i = 1; i < wrappedText.length; i++) {
              if (yPosition > pageHeight - 20) {
                pdf.addPage()
                yPosition = margin
              }
              pdf.text(wrappedText[i], margin + 20, yPosition)
              yPosition += 6
            }
          }
          
          pdf.text(`Added: ${new Date(cutter.created_at).toLocaleDateString()}`, margin + 20, yPosition)
          pdf.text(`Photo: ${cutter.photo_url ? 'Yes' : 'No'}`, margin + 100, yPosition)
          yPosition += 10
        })
      } else {
        pdf.setFontSize(11)
        pdf.text('No cookie cutters in this bin.', margin + 10, yPosition)
        yPosition += 12
      }
      
      // Add separator between bins
      yPosition += 10
      if (binIndex < bins.length - 1) {
        pdf.text('_'.repeat(80), margin, yPosition)
        yPosition += 15
      }
    })
    
    // Download
    const fileName = `complete-inventory-${new Date().toISOString().split('T')[0]}.pdf`
    pdf.save(fileName)
  } catch (error) {
    console.error('Error generating PDF:', error)
    throw error
  }
}

export const exportAllBinsToExcel = (bins: StorageBin[]) => {
  try {
    const workbook = XLSX.utils.book_new()
    
    // Summary Sheet
    const totalCutters = bins.reduce((sum, bin) => sum + (bin.cookie_cutters?.length || 0), 0)
    
    const summary = [
      ['Complete Storage Bins Inventory Report'],
      [''],
      ['Report Generated', new Date().toLocaleString()],
      ['Total Storage Bins', bins.length],
      ['Total Cookie Cutters', totalCutters],
      [''],
      ['Storage Bins Summary:'],
      ['Bin Name', 'Description', 'Cookie Cutters Count', 'Created Date'],
    ]
    
    bins.forEach(bin => {
      summary.push([
        bin.name,
        bin.description || 'N/A',
        bin.cookie_cutters?.length || 0,
        new Date(bin.created_at).toLocaleDateString()
      ])
    })
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summary)
    summarySheet['!cols'] = [
      { wch: 25 },  // Bin Name
      { wch: 40 },  // Description
      { wch: 15 },  // Count
      { wch: 12 }   // Date
    ]
    
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')
    
    // Create individual sheets for each storage bin with contents
    bins.forEach(bin => {
      if (bin.cookie_cutters && bin.cookie_cutters.length > 0) {
        // Create sheet data for this bin
        const binData = [
          [`Storage Bin: ${bin.name}`],
          [''],
          ['Bin Information:'],
          ['Description', bin.description || 'N/A'],
          ['Created Date', new Date(bin.created_at).toLocaleDateString()],
          ['Total Cookie Cutters', bin.cookie_cutters.length],
          [''],
          ['Cookie Cutters in This Bin:'],
          ['#', 'Name', 'Category', 'Description', 'Added Date', 'Has Photo']
        ]
        
        // Add each cookie cutter
        bin.cookie_cutters.forEach((cutter, index) => {
          binData.push([
            index + 1,
            cutter.name,
            cutter.category || 'Uncategorized',
            cutter.description || '',
            new Date(cutter.created_at).toLocaleDateString(),
            cutter.photo_url ? 'Yes' : 'No'
          ])
        })
        
        const binSheet = XLSX.utils.aoa_to_sheet(binData)
        binSheet['!cols'] = [
          { wch: 5 },   // #
          { wch: 25 },  // Name
          { wch: 15 },  // Category
          { wch: 40 },  // Description
          { wch: 12 },  // Added Date
          { wch: 10 }   // Has Photo
        ]
        
        // Truncate sheet name to fit Excel's 31 character limit and make it safe
        const sheetName = bin.name.length > 28 ? bin.name.substring(0, 28) + '...' : bin.name
        const safeSheetName = sheetName.replace(/[\\/*?[\]:]/g, '_') // Remove invalid characters
        XLSX.utils.book_append_sheet(workbook, binSheet, safeSheetName)
      }
    })
    
    // All cutters in one comprehensive sheet
    const allCuttersData: any[] = []
    bins.forEach(bin => {
      if (bin.cookie_cutters) {
        bin.cookie_cutters.forEach((cutter) => {
          allCuttersData.push({
            'Storage Bin': bin.name,
            'Bin Description': bin.description || '',
            'Cutter Name': cutter.name,
            'Category': cutter.category || 'Uncategorized',
            'Description': cutter.description || '',
            'Added Date': new Date(cutter.created_at).toLocaleDateString(),
            'Has Photo': cutter.photo_url ? 'Yes' : 'No'
          })
        })
      }
    })
    
    if (allCuttersData.length > 0) {
      const allCuttersSheet = XLSX.utils.json_to_sheet(allCuttersData)
      allCuttersSheet['!cols'] = [
        { wch: 20 },  // Storage Bin
        { wch: 25 },  // Bin Description
        { wch: 25 },  // Cutter Name
        { wch: 15 },  // Category
        { wch: 40 },  // Description
        { wch: 12 },  // Added Date
        { wch: 10 }   // Has Photo
      ]
      
      XLSX.utils.book_append_sheet(workbook, allCuttersSheet, 'All Cookie Cutters')
    }
    
    // Download
    const fileName = `complete-inventory-${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
  } catch (error) {
    console.error('Error generating Excel:', error)
    throw error
  }
}